import { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { CONTACT } from "@/data/content";

const S = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-head text-lg md:text-lg text-nv uppercase tracking-wide mb-3">{title}</h2>
    <div className="text-sm text-neutral-400 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Gdpr() {
  useEffect(() => {
    document.title = "Ochrana osobních údajů — GATE ESCAPE / HUBERT";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain-layer vignette min-h-screen bg-void text-neutral-200" data-testid="gdpr-page">
      <div className="max-w-3xl mx-auto px-5 py-16">
        <a href="/" data-testid="gdpr-back" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-neutral-500 hover:text-nv transition-colors mb-10">
          <ArrowLeft className="w-4 h-4" /> Zpět na web
        </a>

        <h1 className="font-head text-3xl sm:text-4xl uppercase tracking-widest text-neutral-100 mb-2 vhs-glitch">Ochrana osobních údajů</h1>
        <p className="text-xs text-neutral-600 mb-10">Zásady zpracování osobních údajů (GDPR). Poslední aktualizace: {new Date().toLocaleDateString("cs-CZ")}.</p>

        <S title="1. Správce osobních údajů">
          <p>Provozovatelem únikové hry HUBERT (značka GATE ESCAPE) a správcem osobních údajů je provozovatel na adrese {CONTACT.address}.</p>
          <p>Kontakt: e-mail <a href={`mailto:${CONTACT.email}`} className="text-nv hover:underline">{CONTACT.email}</a>, telefon <a href={CONTACT.phoneHref} className="text-nv hover:underline">{CONTACT.phone}</a>.</p>
        </S>

        <S title="2. Jaké údaje zpracováváme">
          <p>Zpracováváme pouze údaje, které nám sami poskytnete při rezervaci, nákupu dárkového poukazu, hodnocení nebo přes kontaktní formulář:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>jméno a příjmení,</li>
            <li>e-mailová adresa,</li>
            <li>telefonní číslo,</li>
            <li>u fyzického dárkového poukazu doručovací adresa,</li>
            <li>obsah zpráv v chatu a kontaktním formuláři,</li>
            <li>údaje o rezervaci (termín, počet hráčů, poznámka),</li>
            <li>technické údaje (IP adresa, cookies — viz níže).</li>
          </ul>
          <p>Platby kartou zpracovává společnost Stripe. Údaje o platební kartě nikdy neukládáme ani k nim nemáme přístup.</p>
        </S>

        <S title="3. Účel a právní základ zpracování">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Vyřízení rezervace a poukazu</strong> — plnění smlouvy (čl. 6 odst. 1 písm. b GDPR).</li>
            <li><strong>Zasílání potvrzení, připomínek a žádostí o hodnocení</strong> — oprávněný zájem, resp. plnění smlouvy.</li>
            <li><strong>Účetnictví a zákonné povinnosti</strong> — plnění právní povinnosti (čl. 6 odst. 1 písm. c GDPR).</li>
            <li><strong>Analytické cookies</strong> — pouze na základě vašeho souhlasu.</li>
          </ul>
        </S>

        <S title="4. Doba uchování">
          <p>Osobní údaje uchováváme po dobu nezbytně nutnou k vyřízení rezervace a po dobu vyžadovanou právními předpisy (zejména účetními a daňovými, obvykle 10 let u daňových dokladů). Údaje z kontaktního formuláře a chatu uchováváme po dobu nezbytnou k vyřízení dotazu.</p>
        </S>

        <S title="5. Předání údajů třetím stranám">
          <p>Vaše údaje předáváme pouze zpracovatelům, které nezbytně potřebujeme k provozu služby:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Stripe</strong> — zpracování online plateb kartou,</li>
            <li><strong>Resend</strong> — odesílání transakčních e-mailů,</li>
            <li><strong>Google</strong> — synchronizace termínů do kalendáře provozovatele.</li>
          </ul>
          <p>Vaše údaje neprodáváme a nepředáváme k marketingovým účelům třetích stran.</p>
        </S>

        <S title="6. Cookies">
          <p>Web používá nezbytné cookies pro svůj provoz. Analytické cookies aktivujeme pouze s vaším souhlasem, který můžete kdykoli odvolat vymazáním cookies ve svém prohlížeči.</p>
        </S>

        <S title="7. Vaše práva">
          <p>V souvislosti se zpracováním osobních údajů máte právo:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>na přístup ke svým údajům,</li>
            <li>na opravu nepřesných údajů,</li>
            <li>na výmaz („právo být zapomenut"),</li>
            <li>na omezení zpracování,</li>
            <li>vznést námitku proti zpracování,</li>
            <li>na přenositelnost údajů,</li>
            <li>podat stížnost u Úřadu pro ochranu osobních údajů (<a href="https://www.uoou.cz" target="_blank" rel="noreferrer" className="text-nv hover:underline">uoou.cz</a>).</li>
          </ul>
          <p>Svá práva uplatníte na e-mailu <a href={`mailto:${CONTACT.email}`} className="text-nv hover:underline">{CONTACT.email}</a>.</p>
        </S>

        <S title="8. Zabezpečení">
          <p>Přijali jsme přiměřená technická a organizační opatření k ochraně vašich údajů proti neoprávněnému přístupu, ztrátě či zneužití. Komunikace probíhá zabezpečeně přes protokol HTTPS.</p>
        </S>

        <p className="text-xs text-neutral-600 mt-12">Tyto zásady mohou být aktualizovány. Aktuální znění je vždy dostupné na této stránce.</p>
      </div>
    </div>
  );
}
